import api from "./api";

export type ConversaTipo = "dm" | "grupo" | "projeto" | "ia";

export interface ParticipanteResumo {
  id: string;
  conversaId: string;
  usuarioId: string;
  papel: "admin" | "membro";
  ultimaLeituraEm: string | null;
  marcadoNaoLido?: boolean;
  silenciado: boolean;
  fixadoEm?: string | null;
  entrouEm: string;
  usuario: {
    id: string;
    nome: string;
    email: string;
    perfil: string;
    fotoUrl?: string | null;
    tarefaAtualId?: string | null;
    tarefaAtualDesde?: string | null;
    tarefaAtual?: {
      id: string;
      titulo: string;
      projeto: { id: string; nome: string } | null;
      /** True quando o usuário logado tem permissão pra abrir o drawer dessa tarefa. */
      podeVer?: boolean;
    } | null;
  };
}

export interface ConversaResumo {
  id: string;
  tipo: ConversaTipo;
  nome: string | null;
  fotoUrl: string | null;
  projetoId: string | null;
  criadoPor: string | null;
  ultimaMsgEm: string | null;
  criadoEm: string;
  atualizadoEm: string;
  projeto?: { id: string; nome: string; cliente: string } | null;
  participantes: ParticipanteResumo[];
  mensagens?: Array<{ texto: string | null; criadoEm: string; autorId: string | null; autorIa: boolean; autor?: { nome: string } | null; anexos: { tipo: string }[] }>;
  naoLidas?: number;
  marcadoNaoLido?: boolean;
}

export interface ChatAnexo {
  id: string;
  tipo: "imagem" | "audio" | "video" | "documento";
  url: string;
  nomeOriginal: string;
  mimeType: string;
  tamanhoBytes: number;
  duracaoSeg: number | null;
  transcricao: string | null;
  transcricaoStatus: string | null;
}

export interface ChatMensagem {
  id: string;
  conversaId: string;
  autorId: string | null;
  autorIa: boolean;
  texto: string | null;
  respondendoA: string | null;
  editadoEm: string | null;
  removidoEm: string | null;
  criadoEm: string;
  autor: { id: string; nome: string; perfil: string; fotoUrl?: string | null } | null;
  resposta?: { id: string; texto: string | null; autor: { id: string; nome: string } | null } | null;
  anexos: ChatAnexo[];
}

// === Conversas ===
export async function listarConversas() {
  const r = await api.get("/chat/conversas");
  return r.data.data as ConversaResumo[];
}
export async function buscarConversa(id: string) {
  const r = await api.get(`/chat/conversas/${id}`);
  return r.data.data as ConversaResumo;
}
export async function abrirOuCriarDM(outroUsuarioId: string) {
  const r = await api.post("/chat/conversas/dm", { outroUsuarioId });
  return r.data.data as ConversaResumo;
}
export async function criarGrupo(nome: string, usuariosIds: string[]) {
  const r = await api.post("/chat/conversas/grupo", { nome, usuariosIds });
  return r.data.data as ConversaResumo;
}
export async function abrirConversaProjeto(projetoId: string) {
  const r = await api.post(`/chat/conversas/projeto/${projetoId}`);
  return r.data.data as ConversaResumo;
}
export async function abrirChatIA() {
  const r = await api.post("/chat/conversas/ia");
  return r.data.data as ConversaResumo;
}
export async function renomearConversa(id: string, nome: string) {
  const r = await api.patch(`/chat/conversas/${id}`, { nome });
  return r.data.data as ConversaResumo;
}
export async function fixarConversa(id: string) {
  await api.post(`/chat/conversas/${id}/fixar`);
}
export async function desfixarConversa(id: string) {
  await api.delete(`/chat/conversas/${id}/fixar`);
}
export async function avisarDigitando(id: string) {
  await api.post(`/chat/conversas/${id}/digitando`);
}
export async function uploadFotoGrupo(conversaId: string, file: File) {
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post(`/chat/conversas/${conversaId}/foto`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data.data as { fotoUrl: string | null };
}
export async function removerFotoGrupo(conversaId: string) {
  await api.delete(`/chat/conversas/${conversaId}/foto`);
}

export async function adicionarParticipantes(conversaId: string, usuariosIds: string[]) {
  const r = await api.post(`/chat/conversas/${conversaId}/participantes`, { usuariosIds });
  return r.data.data as ConversaResumo;
}
export async function removerParticipante(conversaId: string, usuarioId: string) {
  await api.delete(`/chat/conversas/${conversaId}/participantes/${usuarioId}`);
}

// === Mensagens ===
export async function listarMensagens(conversaId: string, opts?: { antesDe?: string; limite?: number }) {
  const params = new URLSearchParams();
  if (opts?.antesDe) params.append("antesDe", opts.antesDe);
  if (opts?.limite) params.append("limite", String(opts.limite));
  const r = await api.get(`/chat/conversas/${conversaId}/mensagens?${params}`);
  return r.data.data as ChatMensagem[];
}
export async function buscarMensagens(conversaId: string, q: string, opts?: { antesDe?: string; limite?: number }) {
  const params = new URLSearchParams();
  params.append("q", q);
  if (opts?.antesDe) params.append("antesDe", opts.antesDe);
  if (opts?.limite) params.append("limite", String(opts.limite));
  const r = await api.get(`/chat/conversas/${conversaId}/mensagens/buscar?${params}`);
  return r.data.data as ChatMensagem[];
}
export async function enviarMensagem(conversaId: string, texto: string, respondendoA?: string) {
  const r = await api.post(`/chat/conversas/${conversaId}/mensagens`, { texto, respondendoA });
  return r.data.data as ChatMensagem;
}
export async function enviarComAnexo(conversaId: string, file: File | Blob, opts?: { texto?: string; nomeOriginal?: string; respondendoA?: string }) {
  const fd = new FormData();
  fd.append("file", file, opts?.nomeOriginal || (file as File).name || "anexo");
  if (opts?.texto) fd.append("texto", opts.texto);
  if (opts?.respondendoA) fd.append("respondendoA", opts.respondendoA);
  const r = await api.post(`/chat/conversas/${conversaId}/mensagens/com-anexo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data.data as ChatMensagem;
}
export async function editarMensagem(id: string, texto: string) {
  const r = await api.patch(`/chat/mensagens/${id}`, { texto });
  return r.data.data as ChatMensagem;
}
export async function removerMensagem(id: string) {
  await api.delete(`/chat/mensagens/${id}`);
}
export async function limparHistoricoIa(conversaId: string) {
  const r = await api.delete(`/chat/conversas/${conversaId}/mensagens`);
  return r.data.data as { removidas: number };
}
export async function marcarLido(conversaId: string) {
  await api.post(`/chat/conversas/${conversaId}/lido`);
}
export async function marcarNaoLido(conversaId: string) {
  await api.post(`/chat/conversas/${conversaId}/nao-lido`);
}
